import { Ticket02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import {
  useGetMyGroupPromos,
  useGetProfile,
  useGetTicketTypesByEventId,
} from '@sunrise-ui/api-client';
import type { Event, Ticket } from '@sunrise-ui/api/events';
import {
  Button,
  ButtonVariants,
  Colors,
  Modal,
  TextBold,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { QrCodeSvg } from 'react-native-qr-svg';

type Props = {
  visible: boolean;
  onDismiss: () => void;
  ticket?: Ticket | null;
  event?: Event | null;
};

export function TicketReceiptModal({
  visible,
  onDismiss,
  ticket,
  event,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();

  // Get ticket types to find the name and price of the user's ticket
  const { data: ticketTypes } = useGetTicketTypesByEventId({
    eventId: event?.id,
  });

  const { data: promos = [] } = useGetMyGroupPromos(event?.community_id);

  // Get the ticket holder's profile for name display
  const { data: ticketHolderProfile } = useGetProfile(ticket?.user_id);

  const ticketType = useMemo(() => {
    if (!ticketTypes || !ticket) return null;
    return ticketTypes.find((type) => type.id === ticket.ticket_type_id);
  }, [ticketTypes, ticket]);

  const formattedDate = useMemo(() => {
    if (!event?.start_date) return '';

    const startDate = DateTime.fromISO(event.start_date);
    const endDate = event.scheduled_end_date
      ? DateTime.fromISO(event.scheduled_end_date)
      : startDate;

    // If same day, show single date
    if (startDate.hasSame(endDate, 'day')) {
      return startDate.toFormat('LLLL d, yyyy');
    }

    // If same month and year, show "October 6 - 10, 2025"
    if (
      startDate.hasSame(endDate, 'month') &&
      startDate.hasSame(endDate, 'year')
    ) {
      return `${startDate.toFormat('LLLL d')} - ${endDate.toFormat('d, yyyy')}`;
    }

    // If same year, show "October 6 - November 10, 2025"
    if (startDate.hasSame(endDate, 'year')) {
      return `${startDate.toFormat('LLLL d')} - ${endDate.toFormat('LLLL d, yyyy')}`;
    }

    // Different years, show full dates
    return `${startDate.toFormat('LLLL d, yyyy')} - ${endDate.toFormat('LLLL d, yyyy')}`;
  }, [event?.start_date, event?.scheduled_end_date]);

  const formattedPrice = useMemo(() => {
    if (!ticketType?.price) return 'Free';
    return `$${ticketType.price.toFixed(2)}`;
  }, [ticketType?.price]);

  if (!ticket || !event) return null;

  return (
    <Modal
      visible={visible}
      hide={onDismiss}
      size="small"
      contentStyle={styles.modalContent}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <HugeiconsIcon
            icon={Ticket02Icon}
            size={32}
            color={Colors.purple1}
            style={styles.headerIcon}
          />
        </View>

        <View style={styles.content}>
          <View style={styles.eventInfo}>
            <TextBold style={styles.eventName}>{event.name}</TextBold>
            <TextReg style={styles.eventDate}>{formattedDate}</TextReg>
          </View>

          <View style={styles.separator} />

          <View style={styles.ticketInfo}>
            <View style={styles.ticketRow}>
              <TextReg style={styles.label}>Name:</TextReg>
              <TextBold style={styles.value}>
                {ticketHolderProfile
                  ? `${ticketHolderProfile.first_name} ${ticketHolderProfile.last_name}`
                  : 'Loading...'}
              </TextBold>
            </View>

            <View style={styles.ticketRow}>
              <TextReg style={styles.label}>Ticket Type:</TextReg>
              <TextBold style={styles.value}>
                {ticketType?.name || 'General Admission'}
              </TextBold>
            </View>

            <View style={styles.ticketRow}>
              <TextReg style={styles.label}>Price:</TextReg>
              <TextBold style={styles.value}>{formattedPrice}</TextBold>
            </View>

            {promos?.length > 0 && (
              <Button
                variant={ButtonVariants.WHITE}
                size="small"
                onPress={() => {
                  onDismiss();
                  router.navigate(`/tickets/group/${promos[0].slug}`);
                }}
                style={styles.viewAllButton}
              >
                Manage Group Tickets
              </Button>
            )}
          </View>

          {ticket.is_checked_in && (
            <View style={styles.checkedInBadge}>
              <TextReg style={styles.checkedInText}>✓ Checked In</TextReg>
            </View>
          )}

          {ticket.token && (
            <View style={styles.qrSection}>
              <View style={styles.qrCodeContainer}>
                <QrCodeSvg
                  value={JSON.stringify({
                    userId: ticket.user_id,
                    token: ticket.token,
                    eventId: event.id,
                  })}
                  frameSize={isMobile ? 200 : 250}
                />
              </View>
              <TextReg style={styles.qrLabel}>
                Present this QR code at check-in
              </TextReg>
            </View>
          )}
        </View>
      </ScrollView>
    </Modal>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    modalContent: {
      width: '100%',
    },
    header: {
      alignItems: 'center',
      marginBottom: 16,
    },
    headerIcon: {
      marginBottom: 0,
    },
    content: {
      paddingHorizontal: 24,
      width: '100%',
      flex: 1,
    },
    eventInfo: {
      alignItems: 'center',
    },
    eventName: {
      fontSize: 18,
      color: Colors.text,
      textAlign: 'center',
      marginBottom: 8,
    },
    eventDate: {
      fontSize: 14,
      color: Colors.text,
      opacity: 0.8,
      textAlign: 'center',
      marginBottom: 4,
    },
    eventLocation: {
      fontSize: 14,
      color: Colors.text,
      opacity: 0.7,
      textAlign: 'center',
    },
    separator: {
      height: 1,
      backgroundColor: Colors.text,
      opacity: 0.2,
      marginVertical: 16,
      width: '100%',
    },
    ticketInfo: {
      gap: 12,
    },
    viewAllButton: {
      marginTop: 8,
      alignSelf: 'center',
    },
    ticketRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    label: {
      fontSize: 14,
      color: Colors.text,
      opacity: 0.8,
    },
    value: {
      fontSize: 14,
      color: Colors.text,
    },
    checkedInBadge: {
      backgroundColor: Colors.green0,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      alignSelf: 'center',
      marginTop: 16,
    },
    checkedInText: {
      color: Colors.tint,
      fontSize: 12,
      fontWeight: '600',
    },
    scrollContent: {
      alignItems: 'center',
      paddingBottom: 20,
    },
    qrSection: {
      alignItems: 'center',
      width: '100%',
      marginTop: 8,
    },
    qrCodeContainer: {
      backgroundColor: Colors.tint,
      padding: isMobile ? 16 : 20,
      borderRadius: 16,
      marginVertical: 16,
    },
    qrLabel: {
      fontSize: isMobile ? 14 : 16,
      textAlign: 'center',
      opacity: 0.8,
    },
  });
